import type { Company, CustomOutput } from "shared/src/DatabaseObjects/DatabaseObjects"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "shared/src/components/shadcn/components/ui/card"
import { Field, FieldLabel, FieldContent } from "shared/src/components/shadcn/components/ui/field"
import { Alert, AlertDescription } from "shared/src/components/shadcn/components/ui/alert"
import { useAppContext } from "shared/src/libs/contextLib/contextLib"
import { ExternalLink } from "lucide-react"

const INTEGRATIONS_REQUIRING_SETUP = new Set(["jobadder"])

function getAvailableIntegrations(company: Company | undefined): string[] {
	if (company == null) {
		return []
	}
	const outputOptions = company.options?.outputs
	if (outputOptions == null) {
		return []
	}
	return Object.values(outputOptions).map((option: CustomOutput) => option.integrationType)
}

export function IntegrationTab() {
	const { company, user } = useAppContext()

	if (user?.role !== "admin") {
		return (
			<div className="space-y-6">
				<Alert>
					<AlertDescription>Only admins can access integrations.</AlertDescription>
				</Alert>
			</div>
		)
	}

	const platforms = getAvailableIntegrations(company).filter((platform) => INTEGRATIONS_REQUIRING_SETUP.has(platform))

	if (platforms.length === 0) {
		return (
			<div className="space-y-6">
				<Alert className="bg-muted">
					<AlertDescription>No integrations on your account require setup.</AlertDescription>
				</Alert>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Integration Setup</CardTitle>
					<CardDescription>Configure external integrations for your account.</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{platforms.map((platform) => (
						<Field
							key={platform}
							orientation="horizontal"
							className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center">
							<FieldLabel className="capitalize">{platform} Integration</FieldLabel>
							<FieldContent className="max-w-md">
								<Button
									variant="outline"
									className="w-fit"
									onClick={() => {
										window.open(`/singlepageview/integration/setup?platform=${platform}`, "_blank")
									}}>
									Configure
									<ExternalLink className="ml-2 h-4 w-4" />
								</Button>
							</FieldContent>
						</Field>
					))}
				</CardContent>
			</Card>
		</div>
	)
}
